import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../services/api";

// Async Thunks for EmpWork CRUD operations

// Get all empworks
export const getAllEmpWorks = createAsyncThunk(
  "empwork/getAllEmpWorks",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/empworks");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch empworks"
      );
    }
  }
);

// Get my empworks (created by logged-in user)
export const getMyEmpWorks = createAsyncThunk(
  "empwork/getMyEmpWorks",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/empworks/my");
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch your empworks"
      );
    }
  }
);

// Get single empwork by ID
export const getEmpWorkById = createAsyncThunk(
  "empwork/getEmpWorkById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/empwork/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch empwork"
      );
    }
  }
);

// Create new empwork
export const createEmpWork = createAsyncThunk(
  "empwork/createEmpWork",
  async (empworkData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/empwork", empworkData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to create empwork"
      );
    }
  }
);

// Update empwork
export const updateEmpWork = createAsyncThunk(
  "empwork/updateEmpWork",
  async ({ id, empworkData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/empwork/${id}`, empworkData);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.response?.data?.errors?.join(", ") || 
        error.message || 
        "Failed to update empwork"
      );
    }
  }
);

// Delete empwork
export const deleteEmpWork = createAsyncThunk(
  "empwork/deleteEmpWork",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/empwork/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to delete empwork"
      );
    }
  }
);

// Search empworks
export const searchEmpWorks = createAsyncThunk(
  "empwork/searchEmpWorks",
  async (searchQuery, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/empworks/search?search=${searchQuery}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to search empworks"
      );
    }
  }
);

// Get empworks with pagination
export const getEmpWorksWithPagination = createAsyncThunk(
  "empwork/getEmpWorksWithPagination",
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/empworks/paginated?page=${page}&limit=${limit}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to fetch empworks"
      );
    }
  }
);

// EmpWork Slice
const empworkSlice = createSlice({
  name: "empwork",
  initialState: {
    empworks: [],
    currentEmpWork: null,
    loading: false,
    error: null,
    success: false,
    message: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
    setCurrentEmpWork: (state, action) => {
      state.currentEmpWork = action.payload;
    },
    clearCurrentEmpWork: (state) => {
      state.currentEmpWork = null;
    },
  },
  extraReducers: (builder) => {
    // Get all empworks
    builder.addCase(getAllEmpWorks.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getAllEmpWorks.fulfilled, (state, action) => {
      state.loading = false;
      state.empworks = action.payload;
      state.error = null;
    });
    builder.addCase(getAllEmpWorks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get my empworks
    builder.addCase(getMyEmpWorks.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getMyEmpWorks.fulfilled, (state, action) => {
      state.loading = false;
      state.empworks = action.payload;
      state.error = null;
    });
    builder.addCase(getMyEmpWorks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get empwork by ID
    builder.addCase(getEmpWorkById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getEmpWorkById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentEmpWork = action.payload;
      state.error = null;
    });
    builder.addCase(getEmpWorkById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create empwork
    builder.addCase(createEmpWork.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(createEmpWork.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.empworks.unshift(action.payload);
      state.message = "EmpWork created successfully";
      state.error = null;
    });
    builder.addCase(createEmpWork.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Update empwork
    builder.addCase(updateEmpWork.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(updateEmpWork.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "EmpWork updated successfully";
      state.empworks = state.empworks.map((empwork) =>
        empwork._id === action.payload._id ? action.payload : empwork
      );
      if (state.currentEmpWork?._id === action.payload._id) {
        state.currentEmpWork = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateEmpWork.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Delete empwork
    builder.addCase(deleteEmpWork.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.success = false;
    });
    builder.addCase(deleteEmpWork.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.message = "EmpWork deleted successfully";
      state.empworks = state.empworks.filter(
        (empwork) => empwork._id !== action.payload
      );
      if (state.currentEmpWork?._id === action.payload) {
        state.currentEmpWork = null;
      }
      state.error = null;
    });
    builder.addCase(deleteEmpWork.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.success = false;
    });

    // Search empworks
    builder.addCase(searchEmpWorks.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(searchEmpWorks.fulfilled, (state, action) => {
      state.loading = false;
      state.empworks = action.payload;
      state.error = null;
    });
    builder.addCase(searchEmpWorks.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Get empworks with pagination
    builder.addCase(getEmpWorksWithPagination.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getEmpWorksWithPagination.fulfilled, (state, action) => {
      state.loading = false;
      state.empworks = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit || 10,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      };
      state.error = null;
    });
    builder.addCase(getEmpWorksWithPagination.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const {
  clearErrors,
  clearMessage,
  resetSuccess,
  setCurrentEmpWork,
  clearCurrentEmpWork,
} = empworkSlice.actions;

export default empworkSlice.reducer;

